/**
 * PDFViewerPanelSnippetView Component - Snippet view (no PDF)
 *
 * Extracted from PDFViewerPanel.tsx
 */

import React from 'react';
import { FileText, StickyNote, Tag, Info, Copy } from 'lucide-react';
import MarkdownRenderer from '../MarkdownRenderer';
import type { Theme } from '../../themes/theme';
import { parseSnippetMarkdown } from '../../utils/snippetMarkdown';

interface PDFViewerPanelSnippetViewProps {
  snippetMarkdown: string;
  detailTitle?: string;
  detailText?: string;
  detailMetadata: Record<string, any>;
  onCopy: () => void;
  theme: Theme;
}

export const PDFViewerPanelSnippetView: React.FC<PDFViewerPanelSnippetViewProps> = ({
  snippetMarkdown,
  detailTitle,
  detailText,
  detailMetadata,
  onCopy,
  theme,
}) => {
  const parsed = parseSnippetMarkdown(snippetMarkdown);
  const mergedMetadata = {
    ...((detailMetadata?.front_matter as Record<string, any>) ?? {}),
    ...(parsed.data as Record<string, any>),
  };
  const title = detailTitle || mergedMetadata.title || 'Fragmento PDF';
  const notes =
    typeof mergedMetadata.notes === 'string' ? mergedMetadata.notes : detailMetadata?.notes;
  const tags = Array.isArray(mergedMetadata.tags) ? mergedMetadata.tags : detailMetadata?.tags;
  const pageNumber = mergedMetadata.page_number || detailMetadata?.page_number;
  const coordinates = mergedMetadata.coordinates || detailMetadata?.coordinates;
  const createdAtValue = mergedMetadata.created_at || detailMetadata?.created_at;
  const updatedAtValue = mergedMetadata.updated_at || detailMetadata?.updated_at;
  const documentName = mergedMetadata.document_name || detailMetadata?.document_name;
  const snippetType = mergedMetadata.snippet_type || detailMetadata?.snippet_type;

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-4xl mx-auto w-full space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-xs tracking-wide font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Fragmento PDF
              </span>
              <h3 className={`text-lg font-semibold mt-1 ${theme.colors.text.primary}`}>{title}</h3>
              {documentName && (
                <p className={`text-xs mt-1 ${theme.colors.text.secondary}`}>
                  Documento: {documentName}
                  {pageNumber ? ` • Página ${pageNumber}` : ''}
                </p>
              )}
            </div>
            {snippetMarkdown && (
              <button
                onClick={onCopy}
                className={`px-3 py-1.5 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors text-xs font-medium flex items-center gap-1`}
              >
                <Copy className="w-3 h-3" />
                Copiar
              </button>
            )}
          </div>

          <div
            className={`relative max-w-3xl w-full px-5 py-3 rounded-xl ${theme.colors.bg.secondary} ${theme.colors.text.primary} border ${theme.colors.border.primary}`}
          >
            <MarkdownRenderer
              content={snippetMarkdown || detailText || '*Sin contenido disponible*'}
              className="text-sm"
            />
          </div>

          {(notes ||
            (tags && tags.length > 0) ||
            coordinates ||
            createdAtValue ||
            updatedAtValue ||
            snippetType) && (
            <div className="space-y-4">
              {notes && (
                <div>
                  <label
                    className={`text-xs tracking-wide font-semibold flex items-center gap-2 ${theme.colors.text.secondary}`}
                  >
                    <StickyNote className="w-4 h-4" />
                    Notas
                  </label>
                  <div
                    className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3`}
                  >
                    <p
                      className={`text-sm ${theme.colors.text.primary} whitespace-pre-wrap leading-relaxed`}
                    >
                      {notes}
                    </p>
                  </div>
                </div>
              )}

              {tags && tags.length > 0 && (
                <div>
                  <label
                    className={`text-xs tracking-wide font-semibold flex items-center gap-2 ${theme.colors.text.secondary}`}
                  >
                    <Tag className="w-4 h-4" />
                    Etiquetas
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {tags.map((tag: string) => (
                      <span
                        key={tag}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(coordinates || snippetType || createdAtValue || updatedAtValue) && (
                <div className={`text-xs space-y-1 ${theme.colors.text.secondary}`}>
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    <span className="font-semibold">Detalles</span>
                  </div>
                  <div className="pl-6 space-y-1">
                    {snippetType && <p>Tipo: {snippetType}</p>}
                    {pageNumber && <p>Página: {pageNumber}</p>}
                    {coordinates && (
                      <div className="space-y-0.5">
                        <p className="font-semibold">Coordenadas (relativas):</p>
                        {Object.entries(coordinates as Record<string, any>).map(([key, value]) => (
                          <p key={key}>
                            {key}: {typeof value === 'number' ? value.toFixed(4) : String(value)}
                          </p>
                        ))}
                      </div>
                    )}
                    {createdAtValue && (
                      <p>Creado: {new Date(createdAtValue).toLocaleString('es-ES')}</p>
                    )}
                    {updatedAtValue && (
                      <p>Actualizado: {new Date(updatedAtValue).toLocaleString('es-ES')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
